/**
 * Utility functions for creating and managing performance spans for observability
 */

// Unique ID generator for spans
const generateSpanId = (): string => `span_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;

// Store active spans with reference to APM objects
const activeSpans: Record<
  string,
  {
    name: string;
    startTime: number;
    metadata?: Record<string, any>;
    apmSpan?: any; // Store reference to APM span object
  }
> = {};

export const startSpan = (name: string, metadata?: Record<string, any>): string => {
  const spanId = generateSpanId();
  const startTime = performance.now();
  let apmSpan;

  // If Elastic APM is available, create a span within current transaction
  if (window.elasticApm) {
    try {
      // Get current transaction or create one if needed
      let transaction = window.elasticApm.getCurrentTransaction();
      if (!transaction) {
        transaction = window.elasticApm.startTransaction('page_interaction', 'user');
      }

      // Now create a span within this transaction
      if (typeof window.elasticApm.startSpan === 'function') {
        apmSpan = window.elasticApm.startSpan(name, 'custom');
      }
      if (apmSpan && metadata) {
        apmSpan.addLabels(metadata);
      }
    } catch (e) {
      console.error('Failed to start APM span:', e);
    }
  }

  activeSpans[String(spanId)] = {
    name,
    startTime,
    metadata,
    apmSpan,
  };

  return spanId;
};

export const endSpan = (spanId: string, data?: Record<string, any>): void => {
  const span = activeSpans[String(spanId)];
  if (!span) return;

  const duration = performance.now() - span.startTime;

  // If we have an APM span, end it properly
  if (span.apmSpan) {
    try {
      // Add any final data as labels
      if (data) {
        span.apmSpan.addLabels({
          ...data,
          duration,
        });
      }
      // End the span - call end() directly on the span object
      span.apmSpan.end();
    } catch (e) {
      console.error('Failed to end APM span:', e);
    }
  }

  delete activeSpans[String(spanId)];
};

// Update failSpan similarly...
export const failSpan = (spanId: string, error: Record<string, any>): void => {
  const span = activeSpans[String(spanId)];
  if (!span) return;

  const duration = performance.now() - span.startTime;

  // Log error data
  console.error(`[SPAN] ${span.name} failed after ${duration.toFixed(2)}ms`, {
    ...span.metadata,
    ...error,
    duration,
  });

  // If we have an APM span, mark it as failed
  if (span.apmSpan) {
    try {
      span.apmSpan.addLabels({
        error: true,
        ...error,
        duration,
      });
      // End the span
      span.apmSpan.end();

      // Optionally capture an error
      if (window.elasticApm) {
        window.elasticApm.captureError(new Error(`${span.name} failed: ${JSON.stringify(error)}`));
      }
    } catch (e) {
      console.error('Failed to handle APM error:', e);
    }
  }

  delete activeSpans[String(spanId)];
};
